import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Loader2, Search, X, Upload, ImageOff } from 'lucide-react';
import api from './axiosConfig';
import UniversalLookup from './UniversalLookup';

// نفس ترتيب أولوية المفاتيح المستخدم فعليًا جوه UniversalLookup.js لعرض
// القيمة/الوصف - بنعيد استخدامه هنا عشان أي جدول حقيقي (assets, locations..)
// يطلع بنفس المنطق
const LOOKUP_VALUE_KEYS = [
  'value', 'mrnum', 'prnum', 'tagoutnum', 'hazardnum', 'precautionnum', 'ponum', 'personid',
  'jpnum', 'role_id', 'location', 'id', 'taxid', 'assetnum', 'safetypnum', 'failure_class',
  'craft_code', 'slanum', 'site_id', 'comp_num', 'toolnum', 'itemnum', 'meter_num', 'feature_id',
  'org_id', 'classificationid',
];
const LOOKUP_LABEL_KEYS = ['description', 'ldesc', 'meter_name', 'name', 'displayname', 'script_name', 'skill_level'];
// 🎯 لو السيرفر قال صراحة أنهي حقل هو الـ value/label الحقيقي (valueKey/
// labelKey من UniversalLookup - جاية أصلاً من value_key/label_key في رد
// /lookups/{type})، بنستخدمه على طول. القايمة الثابتة تحت دي fallback
// بس لأي نوع لوكاب السيرفر لسه ملوش تعريف فيه (زي /domains/{id} أو
// inventory)
const extractLookupValue = (item, valueKey) => {
  if (valueKey && item[valueKey] != null && item[valueKey] !== '') return item[valueKey];
  return LOOKUP_VALUE_KEYS.map((k) => item[k]).find((v) => v != null && v !== '') ?? item.id;
};
const extractLookupLabel = (item, labelKey) => {
  if (labelKey && item[labelKey] != null && item[labelKey] !== '') return item[labelKey];
  return LOOKUP_LABEL_KEYS.map((k) => item[k]).find((v) => v != null && v !== '');
};

// 🎯 خيار "فلترة بالسايت الحالي" للينك من نوع core_table - نفس فكرة
// extraFilters اللي WorkOrderDetails.js بتستخدمها فعليًا مع handleSearch
const getSiteFilter = (enabled) => {
  if (!enabled) return {};
  try {
    const session = JSON.parse(localStorage.getItem('user') || '{}');
    return session.site_id ? { site_id: session.site_id } : {};
  } catch {
    return {};
  }
};

// 🎯 مودال بحث مصغّر لموديل كستم تاني (لينك من نوع custom_model) - نفس فكرة
// UniversalLookup بس أبسط وموجّه لـ /custom-records/search بدل /lookups
const CustomObjectSearchModal = ({ isOpen, onClose, slug, title, onSelect }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !slug) return;
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api.get('/custom-records/search', { params: { model_slug: slug, search } })
        .then((res) => { if (alive) setResults(res.data || []); })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [isOpen, slug, search]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white w-[420px] shadow-2xl border border-gray-300 flex flex-col max-h-[70vh] rounded-sm overflow-hidden">
        <div className="bg-[#1a3353] text-white px-4 py-3 flex justify-between items-center">
          <span className="text-xs font-bold uppercase tracking-wider">Select {title}</span>
          <button onClick={onClose} className="hover:bg-red-600 p-1 rounded-full transition-colors"><X size={16} /></button>
        </div>
        <div className="p-3 border-b border-gray-200">
          <input
            autoFocus
            className="w-full border border-gray-300 p-2 text-xs outline-none focus:border-blue-600 rounded"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="overflow-y-auto flex-1 min-h-[200px]">
          {loading ? (
            <div className="p-8 text-center text-gray-400"><Loader2 size={18} className="animate-spin inline" /></div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-xs font-bold uppercase">No records</div>
          ) : (
            results.map((r) => (
              <div key={r.id} onClick={() => onSelect(r)} className="px-4 py-2.5 border-b border-gray-100 hover:bg-[#fff9db] cursor-pointer text-sm font-bold text-gray-700">
                {r.label}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const LinkFieldInput = ({ field, value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const linkType = field.options?.link_type || 'core_table';
  const displayLabel = value?.label || '';

  // لو الحقل لسه مش متظبط من الاستوديو (مفيش core_table/domain_id/
  // custom_model_slug مختار) - منفتحش أي بحث خالص، عشان كده كان بيضرب
  // /lookups/ (type فاضي) ويرجع 404
  const isConfigured =
    (linkType === 'core_table' && Boolean(field.options?.core_table)) ||
    (linkType === 'domain' && Boolean(field.options?.domain_id)) ||
    (linkType === 'custom_model' && Boolean(field.options?.custom_model_slug));

  const handleSelectCore = (item, meta) => {
    const val = extractLookupValue(item, meta?.valueKey);
    onChange({ id: val, label: extractLookupLabel(item, meta?.labelKey) || String(val) });
    setIsOpen(false);
  };
  const handleSelectCustomObject = (item) => {
    onChange({ id: item.id, label: item.label });
    setIsOpen(false);
  };

  return (
    <>
      <div className="relative">
        <input
          readOnly
          disabled={disabled}
          value={displayLabel}
          onClick={() => !disabled && isConfigured && setIsOpen(true)}
          placeholder={isConfigured ? '—' : 'Not configured yet'}
          title={isConfigured ? '' : 'This link field has no target set in the Studio yet'}
          className="outline-none pe-6 font-black text-primary uppercase text-[0.9] w-full disabled:opacity-50 cursor-pointer bg-transparent"
        />
        {!disabled && isConfigured && (
          <button type="button" onClick={() => setIsOpen(true)} className="absolute end-0 top-1/2 -translate-y-1/2 text-primary-hover hover:text-primary transition-colors">
            <Search size={14} />
          </button>
        )}
      </div>
      {isConfigured && (linkType === 'custom_model' ? (
        <CustomObjectSearchModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          slug={field.options?.custom_model_slug}
          title={field.label}
          onSelect={handleSelectCustomObject}
        />
      ) : (
        <UniversalLookup
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={field.label}
          type={linkType === 'core_table' ? field.options?.core_table : ''}
          domainId={linkType === 'domain' ? field.options?.domain_id : ''}
          filters={getSiteFilter(linkType === 'core_table' && field.options?.filter_by_site)}
          onSelect={handleSelectCore}
        />
      ))}
    </>
  );
};

// 🎯 نفس فكرة LinkFieldInput بالظبط بس لخلية جوه Table field - العمود
// نفسه بيبقى من نوع "link" (مش الجدول ككل) عشان صف واحد يقدر يشاور على
// سجل حقيقي (مثال: عمود "القطعة" في جدول قطع الغيار بيفتح بحث في Items)
const TableLinkCell = ({ col, value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const linkType = col.link_type || 'core_table';
  const isConfigured =
    (linkType === 'core_table' && Boolean(col.core_table)) ||
    (linkType === 'domain' && Boolean(col.domain_id)) ||
    (linkType === 'custom_model' && Boolean(col.custom_model_slug));

  const handleSelectCore = (item, meta) => {
    const val = extractLookupValue(item, meta?.valueKey);
    onChange({ id: val, label: extractLookupLabel(item, meta?.labelKey) || String(val) });
    setIsOpen(false);
  };
  const handleSelectCustomObject = (item) => {
    onChange({ id: item.id, label: item.label });
    setIsOpen(false);
  };

  return (
    <>
      <div className="relative">
        <input
          readOnly
          disabled={disabled}
          value={value?.label || ''}
          onClick={() => !disabled && isConfigured && setIsOpen(true)}
          placeholder={isConfigured ? '—' : 'Not configured'}
          className="w-full outline-none border border-gray-200 focus:border-primary rounded ps-2 pe-6 py-1 font-bold text-gray-700 bg-white disabled:opacity-50 cursor-pointer text-xs"
        />
        {!disabled && isConfigured && (
          <Search size={11} className="absolute end-1.5 top-1/2 -translate-y-1/2 text-gray-400" />
        )}
      </div>
      {isConfigured && (linkType === 'custom_model' ? (
        <CustomObjectSearchModal isOpen={isOpen} onClose={() => setIsOpen(false)} slug={col.custom_model_slug} title={col.label} onSelect={handleSelectCustomObject} />
      ) : (
        <UniversalLookup
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={col.label}
          type={linkType === 'core_table' ? col.core_table : ''}
          domainId={linkType === 'domain' ? col.domain_id : ''}
          filters={getSiteFilter(linkType === 'core_table' && col.filter_by_site)}
          onSelect={handleSelectCore}
        />
      ))}
    </>
  );
};

// 🎯 رندرر الحقل الواحد - نفس الكومبوننت ده مستخدم في كل مكان بيعرض حقول
// كستم (CustomFieldSlot لتوسيع شاشة موجودة، وCustomModelForm للتطبيقات
// المستقلة) عشان أي نوع حقل جديد يتضاف مكان واحد بس.
//
// نوع "table" (سجلات فرعية/Child Table زي ERPNext) مخزّن كـ array من
// الـ objects جوه نفس عمود data الـ JSONB بتاع السجل الأب - مفيش جدول SQL
// منفصل ولا علاقة FK حقيقية، اتساقًا مع قرار التخزين العام للمشروع كله
// (JSONB بدل جداول ديناميكية). الأعمدة إما بتتعرّف يدوي (options.columns)
// أو بتتجاب من موديل كستم تاني موجود فعلاً (options.linked_model_slug) -
// "افتح كل الأوبجيكت عندي" زي ما اتطلب.
const useLinkedModelColumns = (linkedSlug) => {
  const [columns, setColumns] = useState(null);
  useEffect(() => {
    if (!linkedSlug) { setColumns(null); return; }
    let alive = true;
    api.get(`/custom-models/${linkedSlug}`)
      .then((res) => {
        if (!alive) return;
        setColumns((res.data.fields || []).map((f) => ({
          field_name: f.field_name,
          label: f.label,
          translations: f.translations,
          field_type: ['number', 'date', 'boolean', 'select'].includes(f.field_type) ? f.field_type : 'text',
          choices: f.field_type === 'select' ? f.options?.choices : undefined,
          number_format: f.field_type === 'number' ? f.options?.number_format : undefined,
        })));
      })
      .catch(() => { if (alive) setColumns([]); });
    return () => { alive = false; };
  }, [linkedSlug]);
  return columns;
};

const DOMAIN_COLUMNS = [
  { field_name: 'value', label: 'Value', field_type: 'text' },
  { field_name: 'description', label: 'Description', field_type: 'text' },
];

const TableFieldInput = ({ field, value, onChange, disabled }) => {
  const { i18n } = useTranslation();
  const childMode = field.options?.child_mode || 'columns';
  const isLinkedModel = childMode === 'linked_model';
  const isLinkedDomain = childMode === 'linked_domain';
  const linkedColumns = useLinkedModelColumns(isLinkedModel ? field.options?.linked_model_slug : null);
  const columns = isLinkedModel ? (linkedColumns || []) : isLinkedDomain ? DOMAIN_COLUMNS : (field.options?.columns || []);
  const rows = Array.isArray(value) ? value : [];
  const [pickerOpen, setPickerOpen] = useState(false);

  if (isLinkedModel && linkedColumns === null) {
    return <div className="flex items-center gap-2 text-[10px] text-gray-400"><Loader2 size={12} className="animate-spin" /> Loading...</div>;
  }

  const updateCell = (rowIdx, colName, cellValue) => {
    const next = rows.map((r, i) => (i === rowIdx ? { ...r, [colName]: cellValue } : r));
    onChange(next);
  };

  const addRow = () => onChange([...rows, {}]);
  const removeRow = (rowIdx) => onChange(rows.filter((_, i) => i !== rowIdx));
  const addDomainRow = (item) => {
    onChange([...rows, { value: item.value, description: item.description }]);
    setPickerOpen(false);
  };

  if (columns.length === 0) {
    return <div className="text-[10px] text-gray-400 italic">No columns defined for this table field yet</div>;
  }

  // 🎯 نفس ستايل الجداول الأصلية في النظام (زي WorkOrderList.js/AssetsList.js) -
  // هيدر primary-dark أبيض بارز بدل هيدر باهت، وعرض كامل صريح
  // 🎯 عرض العمود نسبي (زي "columns" في الـ Grid بتاع ERPNext) - كل عمود
  // ليه وزن (col_size، الافتراضي 1)، وعرضه الفعلي = وزنه ÷ مجموع كل
  // الأوزان × 100% - مش بكسل ثابت عشان يفضل متجاوب مع عرض الشاشة
  const totalSize = columns.reduce((sum, col) => sum + (col.col_size || 1), 0) || 1;

  return (
    <div className="border border-line rounded-card shadow-card overflow-hidden w-full">
      <table className="w-full text-xs border-collapse">
        <colgroup>
          {columns.map((col) => (
            <col key={col.field_name} style={{ width: `${((col.col_size || 1) / totalSize) * 100}%` }} />
          ))}
          {!disabled && <col className="w-8" />}
        </colgroup>
        <thead className="bg-primary-dark text-white font-bold uppercase">
          <tr>
            {columns.map((col) => (
              <th key={col.field_name} className="p-2.5 text-left text-[9px] border-r border-white/10 tracking-widest">{col.translations?.[i18n.language] || col.label}</th>
            ))}
            {!disabled && <th className="w-8" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="border-b border-line-subtle last:border-0 hover:bg-primary-soft/40 transition-colors">
              {columns.map((col) => {
                const cellDisabled = disabled || col.is_readonly;
                return (
                <td key={col.field_name} className="p-1.5">
                  {isLinkedDomain ? (
                    <span className="font-bold text-gray-700">{row[col.field_name] ?? ''}</span>
                  ) : col.field_type === 'boolean' ? (
                    <input
                      type="checkbox"
                      disabled={cellDisabled}
                      checked={Boolean(row[col.field_name])}
                      onChange={(e) => updateCell(rowIdx, col.field_name, e.target.checked)}
                      className="accent-primary"
                    />
                  ) : col.field_type === 'link' ? (
                    <TableLinkCell col={col} value={row[col.field_name]} onChange={(v) => updateCell(rowIdx, col.field_name, v)} disabled={cellDisabled} />
                  ) : col.field_type === 'select' ? (
                    <select
                      disabled={cellDisabled}
                      className="w-full outline-none border border-gray-200 focus:border-primary rounded px-2 py-1 font-bold text-gray-700 bg-white disabled:opacity-50 text-xs"
                      value={row[col.field_name] ?? ''}
                      onChange={(e) => updateCell(rowIdx, col.field_name, e.target.value)}
                    >
                      <option value="">—</option>
                      {(col.choices || []).map((choice) => <option key={choice} value={choice}>{choice}</option>)}
                    </select>
                  ) : (
                    <input
                      type={col.field_type === 'number' ? 'number' : col.field_type === 'date' ? 'date' : 'text'}
                      step={col.field_type === 'number' ? (col.number_format && col.number_format !== 'integer' ? 'any' : '1') : undefined}
                      disabled={cellDisabled}
                      className="w-full outline-none border border-gray-200 focus:border-primary rounded px-2 py-1 font-bold text-gray-700 bg-white disabled:opacity-50 disabled:bg-gray-50"
                      value={row[col.field_name] ?? ''}
                      onChange={(e) => updateCell(rowIdx, col.field_name, col.field_type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
                    />
                  )}
                </td>
                );
              })}
              {!disabled && (
                <td className="p-1.5 text-center">
                  <button type="button" onClick={() => removeRow(rowIdx)} className="text-gray-300 hover:text-status-danger">
                    <Trash2 size={13} />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="p-3 text-center text-gray-400 text-[10px] font-bold">No rows yet</td>
            </tr>
          )}
        </tbody>
      </table>
      {!disabled && isLinkedDomain && (
        <UniversalLookup isOpen={pickerOpen} onClose={() => setPickerOpen(false)} title={field.label} domainId={field.options?.domain_id || ''} onSelect={addDomainRow} />
      )}
      {!disabled && (
        <button
          type="button"
          onClick={isLinkedDomain ? () => setPickerOpen(true) : addRow}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-[10px] font-black uppercase text-primary hover:bg-primary-soft transition-colors border-t border-line"
        >
          <Plus size={12} /> {isLinkedDomain ? 'Add from Domain' : 'Add Row'}
        </button>
      )}
    </div>
  );
};

// 🎯 نفس فكرة رفع صورة الأصل (main.py: /assets/upload-image) بس عامة -
// بترجع الرابط بس، والقيمة (رابط الصورة) بتتخزن جوه data الحقل زي أي حقل
// تاني، مفيش عمود ثابت في الداتابيز لازم نحدّثه
const ImageFieldInput = ({ value, onChange, disabled }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/custom-uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url);
    } catch (err) {
      // silent - upload button stays available to retry
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <div className="relative group/img">
          <img src={value} alt="" className="w-20 h-20 object-cover rounded-lg border border-line" />
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="absolute -top-1.5 -end-1.5 bg-status-danger text-white rounded-full p-0.5 opacity-0 group-hover/img:opacity-100 transition-opacity"
            >
              <X size={12} />
            </button>
          )}
        </div>
      ) : (
        <div className="w-20 h-20 rounded-lg border border-dashed border-line flex items-center justify-center text-gray-300">
          <ImageOff size={20} />
        </div>
      )}
      {!disabled && (
        <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-control border border-line text-[10px] font-black uppercase text-ink-secondary hover:bg-primary-soft hover:text-primary cursor-pointer transition-colors">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {value ? 'Replace' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => handleUpload(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
};

const FieldInput = ({ field, value, onChange, disabled }) => {
  // 🎯 نفس ستايل الحقول الأصلية بالظبط (EditableField في WorkOrderDetails.js):
  // مفيش بوردر على الـ input نفسه - البوردر والهوفر بقوا على الصف كله
  // (CustomModelForm.js) زي ما شاشات الورك اوردر/الأصول شغالة فعليًا
  const common = {
    disabled,
    required: field.is_required,
    className: "outline-none bg-transparent w-full font-black text-primary uppercase text-[0.9] disabled:opacity-50 disabled:cursor-not-allowed",
  };

  switch (field.field_type) {
    case 'textarea':
      return (
        <textarea
          disabled={disabled}
          required={field.is_required}
          rows={3}
          className="outline-none bg-transparent w-full font-bold text-ink text-[0.85] resize-none disabled:opacity-50 disabled:cursor-not-allowed"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'number':
      return <input {...common} type="number" step={field.options?.number_format && field.options.number_format !== 'integer' ? 'any' : '1'} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'date':
      return <input {...common} type="date" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
    case 'datetime':
      return <input {...common} type="datetime-local" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
    case 'boolean':
      return (
        <input
          type="checkbox"
          disabled={disabled}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-primary disabled:opacity-50"
        />
      );
    case 'select':
      return (
        <select {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.options?.choices || []).map((choice) => <option key={choice} value={choice}>{choice}</option>)}
        </select>
      );
    case 'table':
      return <TableFieldInput field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'link':
      return <LinkFieldInput field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'image':
      return <ImageFieldInput value={value} onChange={onChange} disabled={disabled} />;
    case 'text':
    default:
      return <input {...common} type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
};

export default FieldInput;
